import React, { useMemo, useState } from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import type { ApplianceDTO, RoutingRedirectDTO } from '@webip/shared/src/dto/appliance';

type Props = {
    appliance: ApplianceDTO;
    redirect?: RoutingRedirectDTO | null;
    onClose: () => void;
    onSave: (data: Omit<RoutingRedirectDTO, 'id' | 'wan_nome' | 'data_criacao' | 'data_ultima_atualizacao'>) => void;
    saving?: boolean;
    fieldErrors?: Record<string, string>;
};

// Espelho do `normalizarDestino` do backend (routing-redirect-destinos.ts), só
// para avisar antes de salvar — a validação que vale é a do backend.
const DOMAIN_LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

function enderecoValido(value: string): boolean {
    if (/^[\d./]+$/.test(value)) {
        const [ip, mask, ...resto] = value.split('/');
        if (resto.length > 0) return false;
        const octetos = ip.split('.');
        if (octetos.length !== 4 || !octetos.every(o => /^\d{1,3}$/.test(o) && Number(o) <= 255)) return false;
        return mask === undefined || (/^\d{1,2}$/.test(mask) && Number(mask) <= 32);
    }
    const labels = value.toLowerCase().replace(/\.$/, '').split('.');
    return labels.length >= 2
        && labels.every(l => DOMAIN_LABEL_RE.test(l))
        && /^[a-z]/.test(labels[labels.length - 1]);
}

function portasValidas(value: string): boolean {
    const [lista, proto, ...resto] = value.toLowerCase().split('/');
    if (resto.length > 0 || (proto !== undefined && proto !== 'tcp' && proto !== 'udp')) return false;
    return lista.split(',').every(p => {
        const m = /^(\d{1,5})(?:-(\d{1,5}))?$/.exec(p.trim());
        if (!m) return false;
        const ini = Number(m[1]);
        const fim = m[2] === undefined ? ini : Number(m[2]);
        return ini >= 1 && fim <= 65535 && ini <= fim;
    });
}

function destinoValido(value: string): boolean {
    const sep = value.indexOf(':');
    if (sep < 0) return enderecoValido(value);
    const endereco = value.slice(0, sep).trim();
    return portasValidas(value.slice(sep + 1)) && (!endereco || enderecoValido(endereco));
}

// Vírgula separa itens, menos entre portas: `:143,587` é um item só.
function parseDestinos(texto: string): string[] {
    const itens: string[] = [];
    for (const token of texto.replace(/\s*,\s*/g, ',').split(/[\s;]+/)) {
        let comPorta = false;
        for (const pedaco of token.split(',')) {
            if (!pedaco) continue;
            if (comPorta && /^\d+(-\d+)?(\/\w+)?$/.test(pedaco)) {
                itens[itens.length - 1] += `,${pedaco}`;
            } else {
                itens.push(pedaco);
                comPorta = pedaco.includes(':');
            }
        }
    }
    return itens;
}

export const RoutingRedirectModal = ({
    appliance,
    redirect,
    onClose,
    onSave,
    saving,
    fieldErrors = {},
}: Props) => {
    const isEditing = !!redirect?.id;

    const [nome, setNome] = useState<string>(redirect?.nome ?? '');
    const [destinosTexto, setDestinosTexto] = useState<string>((redirect?.destinos ?? []).join('\n'));
    const [wanId, setWanId] = useState<string | null>(redirect?.wan_id ? String(redirect.wan_id) : null);
    const [enabled, setEnabled] = useState<boolean>(redirect?.enabled ?? true);
    const [order, setOrder] = useState<string>(String(redirect?.order ?? 100));
    const [comment, setComment] = useState<string>(redirect?.comment ?? '');

    const wanOptions = (appliance.redesWan ?? []).map(w => ({
        id: String(w.id),
        label: `${w.nome} (${w.tipoWan})`,
    }));

    const destinos = useMemo(() => parseDestinos(destinosTexto), [destinosTexto]);
    const invalidos = useMemo(() => destinos.filter(d => !destinoValido(d)), [destinos]);

    const handleConfirm = () => {
        if (!wanId) return;
        onSave({
            appliance_id: appliance.id!,
            wan_id: Number(wanId),
            nome: nome.trim(),
            destinos,
            enabled,
            order: Number(order) || 100,
            comment: comment || undefined,
        });
    };

    const destinosError = invalidos.length
        ? `Não é IP, CIDR, domínio nem :porta: ${invalidos.join(', ')}`
        : fieldErrors.destinos;
    const wanError = fieldErrors.wan_id;

    return (
        <Modal
            show
            title={isEditing ? 'Editar redirecionamento' : 'Novo redirecionamento'}
            onConfirm={handleConfirm}
            onCancel={onClose}
            onClose={onClose}
            confirmProps={{ label: saving ? 'Salvando…' : 'Salvar', disabled: saving || !wanId || !nome.trim() || destinos.length === 0 || invalidos.length > 0, variant: 'primary' }}
            cancelProps={{ label: 'Cancelar' }}
        >
            <VBox style={{ gap: '16px', minWidth: '360px' }}>
                <CampoTexto
                    label="Nome"
                    value={nome}
                    onChange={setNome}
                    placeholder="Ex: Bancos pela VIVO"
                    maxLength={255}
                    error={fieldErrors.nome}
                />

                <CampoMemo
                    label="Destinos"
                    value={destinosTexto}
                    onChange={setDestinosTexto}
                    rows={6}
                    placeholder={'Um por linha. Ex:\nwww.banco.com.br\n200.1.2.3\n200.1.4.0/24\n:25,465,587\nimap.empresa.com.br:993'}
                    hint="IP, CIDR ou domínio — um por linha. Domínio vale só pelo nome exato: inclua cada subdomínio usado (www., api.…). Para limitar a portas, acrescente :porta (vírgula separa portas, a-b é faixa, /udp no fim para UDP — o padrão é TCP); só :porta vale para qualquer destino fora da LAN. Para fixar o link de um dispositivo, use a afinidade de WAN no cadastro dele."
                    error={destinosError}
                />

                <CampoSeletor
                    label="Link WAN de saída"
                    options={wanOptions}
                    value={wanId}
                    onChange={v => setWanId(v)}
                    placeholder="Selecione o link"
                    error={wanError}
                    nullable
                />

                <CampoNumero
                    label="Ordem (prioridade)"
                    value={order}
                    onChange={setOrder}
                    somentePositivos
                    placeholder="100"
                />

                <CampoTexto
                    label="Comentário (opcional)"
                    value={comment}
                    onChange={setComment}
                    placeholder="Descrição do redirecionamento"
                />

                <CampoCheckbox
                    text="Habilitado"
                    value={enabled}
                    onChange={setEnabled}
                />
            </VBox>
        </Modal>
    );
};
